import { Settings } from 'lucide-react'
import { useState } from 'react'

import { Logo } from '@/components/brand/Logo'
import {
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
} from '@/components/ui/sidebar'
import { ToggleTabs } from '@/components/ui/ToggleTabs'
import { cn } from '@/lib/utils'
import { NavRows } from './NavRows'
import { ProductTab, PROTOTYPE_TABS, TAB_ENTITY, TAB_LABEL } from './prototypeNav'

interface PrototypeSidebarProps {
  orgName: string
  /** The row showing in the main area, or null for the dashboard. */
  page: string | null
  onNavigate: (page: string | null) => void
  /** Slot above the product tabs — where a nav-item agent would sit. */
  lead?: React.ReactNode
}

/**
 * The real side nav, rendered with production's own sidebar primitives and fed
 * prototype rows instead of the live user — so a tab can drop its landing page
 * here without touching anything shipped.
 *
 * Rows are buttons rather than the app's `SidebarNavItem`, which renders a
 * router `Link`: inside the prototype a real link walks the reviewer out of the
 * page they came to look at.
 */
export function PrototypeSidebar({ orgName, page, onNavigate, lead }: PrototypeSidebarProps) {
  const [tab, setTab] = useState<ProductTab>(ProductTab.INSURANCE)

  return (
    <div className="flex h-full w-(--sidebar-width) shrink-0 flex-col border-r bg-white">
      <SidebarHeader className="gap-3 overflow-hidden">
        <div className="flex min-h-8 items-center">
          <Logo size="small" width={44} className="ml-2" />
        </div>
        {lead}
        <div className="flex flex-col gap-3">
          {/* The Dashboard row is gone under this placement, so the carrier name carries it. */}
          <button
            type="button"
            onClick={() => onNavigate(null)}
            aria-current={page === null ? 'page' : undefined}
            className={cn(
              'hover:bg-sidebar-accent text-ink-900 truncate rounded-md px-2 py-1 text-left text-sm font-medium capitalize',
              page === null && 'bg-sidebar-accent'
            )}
          >
            {orgName}
          </button>
          <ToggleTabs
            value={tab}
            onValueChange={(next) => setTab(next as ProductTab)}
            listClassName="border-ink-50 w-full flex-wrap gap-0.5 data-[orientation=horizontal]:h-auto"
            triggerClassName="h-7 flex-auto cursor-pointer px-2 text-[10px]"
            items={PROTOTYPE_TABS.map((entry) => ({ value: entry, label: TAB_LABEL[entry] }))}
          />
        </div>
      </SidebarHeader>

      <SidebarContent className="no-scrollbar gap-0">
        <SidebarSeparator className="bg-border" />
        <NavRows tab={tab} page={page} onNavigate={onNavigate} />
        <p className="text-ink-300 mt-auto truncate px-4 py-3 text-[10px] leading-4">{TAB_ENTITY[tab]}</p>
      </SidebarContent>

      <SidebarSeparator className="bg-border" />
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton className="text-xs">
              <Settings color="var(--color-ink-300)" />
              <span>Admin Settings</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </div>
  )
}
